/**
 * Shared live chart-anchor position in chart-container pixels. The chart's
 * render loop writes the price-line tip here every frame (cost: two number
 * assignments); the PnL pip reads it and positions itself WITHOUT any
 * per-frame getBoundingClientRect, so it no longer forces a synchronous
 * reflow against the 60fps chart.
 *
 * Only one chart is mounted at a time (live TrendChart XOR candle
 * TradingViewChart), so there's a single writer; each clears the anchor
 * on unmount.
 */
interface ChartAnchor {
    /** Line-tip X in chart-container CSS pixels. */
    x: number;
    /** Line-tip Y in chart-container CSS pixels. */
    y: number;
    /** rAF/interval timestamp (ms) of the last write; stale ⇒ hide. */
    updatedAt: number;
    valid: boolean;
}

export const chartAnchor: ChartAnchor = {
    x: 0,
    y: 0,
    updatedAt: 0,
    valid: false,
};

/**
 * The pip registers a consumer here and is positioned SYNCHRONOUSLY from
 * the chart's own render pass (via setChartAnchor) — frame-perfect, no
 * separate rAF that could trail the chart by a frame.
 */
type AnchorConsumer = (x: number, y: number, valid: boolean) => void;
let consumer: AnchorConsumer | null = null;

export function registerAnchorConsumer(fn: AnchorConsumer | null): void {
    consumer = fn;
}

export function setChartAnchor(x: number, y: number, now: number): void {
    chartAnchor.x = x;
    chartAnchor.y = y;
    chartAnchor.updatedAt = now;
    chartAnchor.valid = true;
    consumer?.(x, y, true);
}

export function clearChartAnchor(): void {
    chartAnchor.valid = false;
    consumer?.(chartAnchor.x, chartAnchor.y, false);
}
